import type { PublicDealsRetailer } from "../config/publicDeals";
import { breadcrumbJsonLd } from "./site";

/**
 * Title/description copy for SPA routes that have no content module of their own.
 * Shared by the pages (useSeo) and the build-time pre-renderer so the HTML a crawler
 * fetches and the rendered page never disagree.
 */
export function publicDealsSeo(retailer: PublicDealsRetailer) {
  const name = retailer.shortName;
  return {
    // The store page of the home feed ("/walmart") is the canonical deals page now.
    path: `/${retailer.slug}`,
    title: `${name} Deals & Price Drops Today — Free to Browse | DealHawk`,
    description: `Browse today's ${name} online deals and price drops for free. Search, filter by discount and category, compare prices, and open retailer listings — no account needed.`,
    jsonLd: [
      breadcrumbJsonLd([
        { name: "Home", path: "/" },
        { name: "Deals", path: "/deals" },
        { name: `${name} deals` },
      ]),
    ],
  };
}

/**
 * Store pages of the logged-out feed: "/walmart", "/walmart/deals", "/walmart/toys".
 * Each gets its own title so store and category searches can land on it.
 */
export function homeFeedSeo(retailer: PublicDealsRetailer, section = "") {
  const name = retailer.displayName ?? (retailer.name.endsWith(" Online") ? retailer.domain : retailer.shortName);
  const base = `/${retailer.slug}`;
  if (section && section !== "deals") {
    const cat = section.split(/[_\s-]+/).filter(Boolean).map((w) => w[0].toUpperCase() + w.slice(1)).join(" ");
    return {
      path: `${base}/${encodeURIComponent(section)}`,
      title: `${cat} Deals at ${name} Today | DealHawk`,
      description: `The newest ${cat.toLowerCase()} markdowns and price drops at ${name}, updated through the day, plus in-store clearance near you.`,
      jsonLd: [breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: `${name} deals`, path: base }, { name: cat }])],
    };
  }
  return {
    path: section === "deals" ? `${base}/deals` : base,
    title: `${name} Deals & Price Drops Today | DealHawk`,
    description: `Today's newest ${name} online deals and price drops by category, updated through the day, plus in-store clearance near you.`,
    jsonLd: [breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: `${name} deals` }])],
  };
}

/**
 * The logged-out home feed at "/". The pre-renderer writes "/" to dist/home.html with
 * these tags; keep them identical to web/index.html, which is the fallback shell.
 */
export const HOME_SEO = {
  path: "/",
  title: "DealHawk - Find In-Store Clearance & Markdowns Near You",
  description:
    "DealHawk scans in-store inventory and clearance prices at Walmart, Target, Best Buy, Dollar General, Costco, and other big box retailers. Find in-store markdowns before anyone else.",
};

export const CLEARANCE_SEO = {
  path: "/clearance",
  title: "In-Store Clearance Near You | DealHawk",
  description:
    "See how many items are marked down on clearance at the Walmart, Target, Best Buy, Costco, Home Depot and Dollar General stores near your ZIP code today.",
};

export const BLOG_INDEX_SEO = {
  path: "/blog",
  title: "DealHawk Blog — Daily Deals, Clearance Finds & Shopping Tips",
  description:
    "The best retail deals and clearance finds from Walmart, Target, Dollar General and more, updated daily by DealHawk.",
  jsonLd: [breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Blog" }])],
};

export function featureSeo(feature: {
  slug: string;
  navLabel: string;
  navDescription: string;
  heroSubtitle: string;
  faq?: { q: string; a: string }[];
}) {
  return {
    path: `/features/${feature.slug}`,
    title: `${feature.navLabel}: ${feature.navDescription} | DealHawk`,
    description: feature.heroSubtitle,
  };
}

export const GUIDES_INDEX_SEO = {
  path: "/guides",
  title: "Clearance & Penny Shopping Guides | DealHawk",
  description:
    "How clearance really works at Home Depot, Dollar General, Walmart, Target, Lowe's, Best Buy and Costco — markdown schedules, price codes, penny items and where to find them.",
};
